// @givmo/board — the Form 990 page: Part VI's questions and what the board's records hold for each
// (CBG-YR5-VIEW; the derivation §2; frames p1, p2, p3 and p9; the backend's `board-form-990.md`).
//
// One tax year at a time. Each line of Part VI is one row: the form's own number and words, then one
// line of what the board recorded that bears on it, or a dash. Selecting a row opens its records in
// place, with the instructions' own words one click further. Ramp's Policy page is the reference for
// the sections (a heading, then quiet rows), Ramp's People table for the dash (the design's own).
//
// ⚠ NEVER HERE (derivation §2.4, I1): an answer, a check, a score, a colour per line, a count of lines,
// an order by what is absent, or the words met, satisfies, complete, ready, compliant, correct, on time,
// late, missing, needs or should. The IRS's words come from the read and are shown as quotations with
// their source (D-CBG-30); every word in Board's own voice is in `form990-words.ts`.
//
// The year is the organization's, never assumed to be the calendar's: from the fiscal year end it
// recorded in Givmo, else from the month and day chosen here, kept only in this page's address
// (`?year_end_month=&year_end_day=`) and stored nowhere. A year is named in the address by its last day
// (`?tax_year_ends_on=`), never its named year: a change of year end can give two tax years one name.
// `?line=8a` opens that line on arrival and brings it into view (the Schedule O draft's "From your
// records" points at line 8a's meetings and consents, CBG-YR5-SCHO).
//
// TWO PARTS, as tabs carried in the address (`?tab=part-vi` · `?tab=compensation`, the Conflicts
// page's precedent; CBG-UX8-MENU, D-CBG-35 (1)): Part VI, the year's questions as above, and the
// board's compensation decisions, moved here from the page that now shows as Self-assessment, beside
// line 15, which already lists each year's. An address with no part opens Part VI, so every earlier
// link here lands where it did; the decisions' old address is replaced by the host. Part VI's read,
// its address rules, its "How this works" and "Provide the return to the board" belong to that part
// alone: the decisions read their own list, for every year, and keep their own lead.

import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  Icon,
  Select,
  Skeleton,
  Tabs,
  type TabsValue,
} from '@givmo/design-system';
import { ChevronDown } from 'lucide-react';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { Link, useNavigate, useSearch } from '../../board-routing';
import { BOARD_PAGES, FORM_990_TABS, type Form990Tab } from '../../board-routes';
import { HowThisWorks } from '../../components/fine-print';
import { PageShell } from '../../components/page-shell';
import { RegisteredDisclaimer } from '../../components/registered-disclaimer';
import { displayDate } from '../../lib/display';
import {
  classSurfaces,
  daysInMonth,
  lineSummary,
  monthDayWords,
  taxYearOption,
  type Form990,
  type Form990Evidence,
  type Form990Line,
  type Form990Question,
  type Form990Quotation,
  type Form990Section,
  type Form990TaxYear,
} from './form990-decode';
import { CompensationPart } from '../compensation/compensation-part';
import { compensationWords as pay } from '../compensation/compensation-words';
import { ProvideDialog, type ProvideTarget } from '../returns/provide-dialog';
import { RETURN_ACT_PERMISSION } from '../returns/return-queries';
import { returnWords as rw } from '../returns/return-words';
import {
  Choices,
  LineEvidence,
  LINK,
  NothingRecordedWay,
  QUIET,
  WhatTheInstructionsSay,
} from './form990-evidence';
import { addressOf, lineParam, searchOf } from './form990-address';
import { useForm990, type Form990Address, type Form990Read } from './form990-queries';
import { form990Words as w } from './form990-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const NUMBER = 'font-sans text-s13 font-w600 tabular-nums tracking-givmo text-text-tertiary';
const ROW_GRID =
  'grid w-full grid-cols-[36px_minmax(0,1fr)_20px] items-start gap-x-12 gap-y-4 px-20 py-12 text-left md:grid-cols-[44px_minmax(0,1fr)_minmax(0,340px)_20px] md:gap-x-16';
const OPENED = 'flex flex-col gap-16 pb-16 pl-[68px] pr-20 md:pl-[80px] md:pr-[56px]';

// ── The page ─────────────────────────────────────────────────────────────────────────────────────

/** The address's refusals (§3.2, §3.3): which part of it the read refused, and its sentence. */
type AddressRefusal = Extract<Form990Read, { kind: 'tax-year-unknown' | 'year-end-refused' }>;

function isAddressRefusal(read: Form990Read | undefined): read is AddressRefusal {
  return (
    read !== undefined && (read.kind === 'tax-year-unknown' || read.kind === 'year-end-refused')
  );
}

/** What the page says after a refused address was dropped, and where: under the year, or the picker. */
interface Notice {
  part: 'year' | 'day';
  sentence: string;
  month: number | null;
}

/**
 * What "Provide the return to the board" provides, or `null` when the page offers no such act: the
 * return's surface is off (`return_copies` null), the year hasn't ended, the year already has a copy
 * (a newer one is provided from that copy's page), or the reader can't act.
 */
function provideTargetOf(page: Form990 | null, canAct: boolean): ProvideTarget | null {
  const taxYear = page?.tax_year ?? null;
  const copies = page?.evidence?.return_copies ?? null;
  if (!canAct || page === null || taxYear === null || copies === null) return null;
  if (!taxYear.ended || copies.length > 0) return null;
  const line11a =
    page.form?.sections
      .flatMap((section) => section.questions)
      .flatMap((question) => question.lines)
      .find((line) => line.evidence_kinds.includes('return_copies')) ?? null;
  return {
    taxYear: {
      begins_on: taxYear.begins_on,
      ends_on: taxYear.ends_on,
      named_year: taxYear.named_year,
    },
    form: '990',
    yearEnd:
      page.year_end.source === 'chosen' &&
      page.year_end.month !== null &&
      page.year_end.day !== null
        ? { month: page.year_end.month, day: page.year_end.day }
        : null,
    followsRecordId: null,
    instructions: line11a?.instructions ?? null,
  };
}

/** The part the address names, or Part VI. */
function form990TabOf(search: Record<string, unknown>): Form990Tab {
  return search.tab === FORM_990_TABS.compensation
    ? FORM_990_TABS.compensation
    : FORM_990_TABS.partVi;
}

export function Form990Page() {
  const { orgRef, isReady, permissions } = useBoardHost();
  const navigate = useNavigate();
  const search = useSearch();
  const tab = form990TabOf(search);
  const onPartVi = tab === FORM_990_TABS.partVi;
  const address = addressOf(search);
  // Part VI's read is that part's alone: the compensation decisions read their own list.
  const query = useForm990(orgRef, address, isReady && onPartVi);
  const read = query.data;
  const [notice, setNotice] = useState<Notice | null>(null);

  const go = (next: Form990Address) =>
    void navigate({ to: BOARD_PAGES.form990, search: searchOf(next), replace: true });

  // Switching parts keeps the year the address names, so coming back to Part VI shows the same one;
  // `replace`, so switching never stacks history.
  const selectTab = (value: TabsValue) =>
    void navigate({
      to: BOARD_PAGES.form990,
      search: {
        ...searchOf(address),
        tab:
          value === FORM_990_TABS.compensation ? FORM_990_TABS.compensation : FORM_990_TABS.partVi,
      },
      replace: true,
    });

  // A last day the address holds that is no calendar day (a hand-edited `2025-02-30`) never reaches
  // the server: it is dropped like a stale year, with the year's own line, and a chosen month and day
  // stay as they were.
  const unreadableYear =
    onPartVi && search.tax_year_ends_on !== undefined && address.taxYearEndsOn === null;
  useEffect(() => {
    if (!unreadableYear) return;
    setNotice({ part: 'year', sentence: w.taxYearRefused, month: null });
    go(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per unreadable day
  }, [unreadableYear]);

  // A refused address is read again without the part refused: a stale tax year without its year; a
  // month and day the page can't read without them (and the year, which they decide). With a year
  // end recorded the day was never used, so nothing is said; with none, the picker says why.
  const refused = onPartVi && isAddressRefusal(read) ? read : null;
  useEffect(() => {
    if (refused === null) return;
    if (refused.kind === 'tax-year-unknown') {
      setNotice({ part: 'year', sentence: refused.sentence ?? w.taxYearRefused, month: null });
      go({ ...address, taxYearEndsOn: null });
    } else {
      setNotice({
        part: 'day',
        sentence: refused.sentence ?? w.yearEndRefused,
        month:
          address.yearEndMonth !== null && address.yearEndMonth >= 1 && address.yearEndMonth <= 12
            ? address.yearEndMonth
            : null,
      });
      go({ taxYearEndsOn: null, yearEndMonth: null, yearEndDay: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per refusal
  }, [refused]);

  const page = read?.kind === 'page' ? read.page : null;
  const [providing, setProviding] = useState(false);
  const provideTarget = provideTargetOf(
    page,
    hasBoardPermission(permissions, RETURN_ACT_PERMISSION),
  );
  const howLines = [
    ...w.howLead,
    ...(page === null ? [] : [page.year_end.source === 'recorded' ? w.howRecorded : w.howChosen]),
    ...(page?.form ? [w.formNote(page.form.form_year)] : []),
  ];
  const chooseDay = (month: number, day: number) => {
    setNotice(null);
    go({ taxYearEndsOn: null, yearEndMonth: month, yearEndDay: day });
  };

  const partVi =
    !isReady || orgRef === null || query.isPending || refused !== null ? (
      <Loading />
    ) : query.isError || read === undefined ? (
      <ReadFailed onRetry={() => void query.refetch()} />
    ) : read.kind === 'not-served' ? (
      <QuietCard text={w.notServed} testId="form990-not-served" />
    ) : read.kind === 'type-refused' ? (
      <QuietCard text={read.sentence ?? w.typeRefused} testId="form990-type-refused" />
    ) : read.kind === 'page' && read.page.tax_year === null ? (
      <YearEndPicker
        month={notice?.part === 'day' ? notice.month : null}
        day={null}
        refused={notice?.part === 'day' ? notice.sentence : null}
        onChoose={chooseDay}
      />
    ) : read.kind === 'page' ? (
      <TheYear
        page={read.page}
        orgRef={orgRef}
        openLine={lineParam(search.line)}
        staleYear={notice?.part === 'year' ? notice.sentence : null}
        onYear={(endsOn) => {
          setNotice(null);
          go({ ...address, taxYearEndsOn: endsOn });
        }}
        onDay={chooseDay}
      />
    ) : null;

  return (
    <PageShell
      title={w.pageTitle}
      description={
        onPartVi ? (
          <>
            {w.pageDescription} <HowThisWorks testId="form990-how" lines={howLines} />
          </>
        ) : (
          w.pageDescription
        )
      }
      actions={
        onPartVi && provideTarget !== null ? (
          <Button
            appearance="saas"
            tone="primary"
            onClick={() => setProviding(true)}
            testId="form990-provide"
          >
            {rw.provideButton}
          </Button>
        ) : undefined
      }
      testId="page-form-990"
    >
      {providing && provideTarget !== null && orgRef !== null ? (
        <ProvideDialog orgRef={orgRef} target={provideTarget} onClose={() => setProviding(false)} />
      ) : null}
      <Tabs.Root appearance="saas" value={tab} onValueChange={selectTab} testId="form990-tabs">
        <Tabs.List aria-label={w.pageTitle}>
          <Tabs.Tab value={FORM_990_TABS.partVi} testId="form990-tab-part-vi">
            {w.partViTab}
          </Tabs.Tab>
          <Tabs.Tab value={FORM_990_TABS.compensation} testId="form990-tab-compensation">
            {pay.compensationTab}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={FORM_990_TABS.partVi} focusable={false}>
          <div className="flex flex-col gap-24 pt-8">{partVi}</div>
        </Tabs.Panel>
        <Tabs.Panel value={FORM_990_TABS.compensation} focusable={false}>
          <CompensationPart />
        </Tabs.Panel>
      </Tabs.Root>
    </PageShell>
  );
}

function Loading() {
  return (
    <div className="flex flex-col gap-24" data-testid="form990-loading">
      <Skeleton className="h-36 w-full max-w-[380px] rounded-lg" />
      <Skeleton className="h-[420px] w-full rounded-xl" />
    </div>
  );
}

function QuietCard({ text, testId }: { text: string; testId: string }) {
  return (
    <Card testId={testId}>
      <CardBody>
        <p className={SECONDARY}>{text}</p>
      </CardBody>
    </Card>
  );
}

function ReadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <Card testId="form990-error">
      <CardBody className="flex flex-col items-start gap-12">
        <p className={SECONDARY}>{w.loadFailed}</p>
        <Button appearance="saas" tone="secondary" onClick={onRetry} testId="form990-error-retry">
          {w.tryAgain}
        </Button>
      </CardBody>
    </Card>
  );
}

// ── The day a tax year ends, when none is recorded ───────────────────────────────────────────────

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1),
  label: new Date(2000, index, 1).toLocaleDateString(undefined, { month: 'long' }),
}));

function YearEndPicker({
  month: initialMonth,
  day: initialDay,
  refused,
  onChoose,
  onCancel,
}: {
  month: number | null;
  day: number | null;
  refused: string | null;
  onChoose: (month: number, day: number) => void;
  onCancel?: () => void;
}) {
  const validMonth = initialMonth !== null && initialMonth >= 1 && initialMonth <= 12;
  const [month, setMonth] = useState<number | null>(validMonth ? initialMonth : null);
  const [day, setDay] = useState<number | null>(
    validMonth && initialDay !== null && initialDay >= 1 && initialDay <= daysInMonth(initialMonth)
      ? initialDay
      : null,
  );
  const days = Array.from({ length: month === null ? 31 : daysInMonth(month) }, (_, index) => ({
    value: String(index + 1),
    label: String(index + 1),
  }));
  return (
    <Card testId="form990-year-end">
      <CardBody className="flex flex-col gap-16">
        <p className={SECONDARY}>{w.noYearEnd}</p>
        <div className="flex flex-wrap items-end gap-12">
          <div className="w-[180px]">
            <Select
              appearance="saas"
              label={w.monthLabel}
              placeholder={w.monthPlaceholder}
              options={MONTH_OPTIONS}
              value={month === null ? null : String(month)}
              onValueChange={(value) => {
                const next = value === null ? null : Number(value);
                setMonth(next);
                if (next !== null && day !== null && day > daysInMonth(next)) setDay(null);
              }}
              testId="form990-year-end-month"
            />
          </div>
          <div className="w-[110px]">
            <Select
              appearance="saas"
              label={w.dayLabel}
              placeholder={w.dayPlaceholder}
              options={days}
              value={day === null ? null : String(day)}
              onValueChange={(value) => setDay(value === null ? null : Number(value))}
              testId="form990-year-end-day"
            />
          </div>
          <Button
            appearance="saas"
            tone="primary"
            disabled={month === null || day === null}
            onClick={() => {
              if (month !== null && day !== null) onChoose(month, day);
            }}
            testId="form990-year-end-show"
          >
            {w.showYears}
          </Button>
          {onCancel !== undefined ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={onCancel}
              testId="form990-year-end-cancel"
            >
              {w.cancel}
            </Button>
          ) : null}
        </div>
        {refused !== null ? (
          <ErrorMessage testId="form990-year-end-refused">{refused}</ErrorMessage>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── One tax year ─────────────────────────────────────────────────────────────────────────────────

/** The Schedule O draft's address for the year shown: its last day, and a chosen day if chosen. */
function scheduleOSearchOf(page: Form990): Record<string, string | number> | null {
  if (page.tax_year === null) return null;
  const { source, month, day } = page.year_end;
  return {
    tax_year_ends_on: page.tax_year.ends_on,
    ...(source === 'chosen' && month !== null && day !== null
      ? { year_end_month: month, year_end_day: day }
      : {}),
  };
}

function TheYear({
  page,
  orgRef,
  openLine,
  staleYear,
  onYear,
  onDay,
}: {
  page: Form990;
  orgRef: string;
  openLine: string | null;
  staleYear: string | null;
  onYear: (endsOn: string) => void;
  onDay: (month: number, day: number) => void;
}) {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
    () => new Set(openLine === null ? [] : [openLine]),
  );
  const [changingDay, setChangingDay] = useState(false);
  // A line the address opened is brought into view once, on arrival.
  useEffect(() => {
    if (openLine === null) return;
    document
      .querySelector(`[data-testid="form990-line-${openLine}"]`)
      ?.scrollIntoView?.({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);
  const taxYear = page.tax_year;
  const form = page.form;
  const evidence = page.evidence;
  if (taxYear === null || form === null || evidence === null) return null;
  const scheduleO = scheduleOSearchOf(page);

  const toggle = (line: string) =>
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });

  const notes = [
    taxYear.ended ? null : w.notEnded(displayDate(taxYear.ends_on)),
    form.form_year === taxYear.named_year ? null : w.formDiffers(form.form_year),
  ].filter((note): note is string => note !== null);

  return (
    <>
      <div className="flex flex-col gap-8">
        <div className="w-full md:w-[380px]">
          <Select
            appearance="saas"
            aria-label={w.yearLabel}
            options={page.tax_years.map((year) => ({
              value: year.ends_on,
              label: taxYearOption(year),
            }))}
            value={taxYear.ends_on}
            onValueChange={(value) => {
              if (value !== null && value !== taxYear.ends_on) onYear(value);
            }}
            testId="form990-year"
          />
        </div>
        {page.year_end.source === 'chosen' && !changingDay ? (
          <p className={QUIET} data-testid="form990-chosen">
            {w.chosenLine(monthDayWords(page.year_end.month ?? null, page.year_end.day ?? null))}{' '}
            <button
              type="button"
              className="font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              onClick={() => setChangingDay(true)}
              data-testid="form990-change-day"
            >
              {w.changeDay}
            </button>
          </p>
        ) : null}
        {notes.length > 0 ? (
          <p className={QUIET} data-testid="form990-year-notes">
            {notes.join(' ')}
          </p>
        ) : null}
        {staleYear !== null ? (
          <p className={QUIET} role="status" data-testid="form990-stale-year">
            {staleYear}
          </p>
        ) : null}
      </div>

      {changingDay ? (
        <YearEndPicker
          month={page.year_end.month ?? null}
          day={page.year_end.day ?? null}
          refused={null}
          onChoose={(month, day) => {
            setChangingDay(false);
            onDay(month, day);
          }}
          onCancel={() => setChangingDay(false)}
        />
      ) : null}

      {form.sections.map((section) => (
        <SectionCard
          key={section.section}
          section={section}
          evidence={evidence}
          taxYear={taxYear}
          quotations={form.no_committee_quotations}
          scheduleO={scheduleO}
          openIds={openIds}
          toggle={toggle}
        />
      ))}

      {/* The consult-counsel slot (§7): the registry's words for each class the read names, only
          once a class is in force; until then nothing draws here at all. */}
      {classSurfaces(page.disclaimer_class_ids).map((surface) => (
        <RegisteredDisclaimer
          key={surface}
          surface={surface}
          orgRef={orgRef}
          testId={`form990-class-${surface}`}
        />
      ))}
    </>
  );
}

function SectionCard({
  section,
  evidence,
  taxYear,
  quotations,
  scheduleO,
  openIds,
  toggle,
}: {
  section: Form990Section;
  evidence: Form990Evidence;
  taxYear: Form990TaxYear;
  quotations: readonly Form990Quotation[];
  scheduleO: Record<string, string | number> | null;
  openIds: ReadonlySet<string>;
  toggle: (line: string) => void;
}) {
  return (
    <Card testId={`form990-section-${section.section}`}>
      <CardHeader>
        <div className="flex min-w-0 flex-col gap-2">
          <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {section.title}
          </h2>
          {section.note !== null && section.note !== undefined ? (
            <p className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              {section.note}
            </p>
          ) : null}
        </div>
        <span className="hidden shrink-0 pt-2 font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary md:block md:w-[376px]">
          {w.inYourRecords}
        </span>
      </CardHeader>
      <CardBody flush>
        <ul className="flex flex-col">
          {section.questions.map((question) => (
            <QuestionRows
              key={question.number}
              question={question}
              evidence={evidence}
              taxYear={taxYear}
              quotations={quotations}
              scheduleO={scheduleO}
              openIds={openIds}
              toggle={toggle}
            />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function QuestionRows({
  question,
  evidence,
  taxYear,
  quotations,
  scheduleO,
  openIds,
  toggle,
}: {
  question: Form990Question;
  evidence: Form990Evidence;
  taxYear: Form990TaxYear;
  quotations: readonly Form990Quotation[];
  scheduleO: Record<string, string | number> | null;
  openIds: ReadonlySet<string>;
  toggle: (line: string) => void;
}) {
  return (
    <>
      {question.stem !== null && question.stem !== undefined ? (
        // A numbered question whose words come before its lettered lines (8 and 15): its own row.
        <li
          className="grid grid-cols-[36px_minmax(0,1fr)] gap-x-12 border-t border-border-subtle px-20 pb-2 pt-12 first:border-t-0 md:grid-cols-[44px_minmax(0,1fr)] md:gap-x-16"
          data-testid={`form990-question-${question.number}`}
        >
          <span className={NUMBER}>{question.number}</span>
          <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
            {question.stem}
          </span>
        </li>
      ) : null}
      {question.lines.map((line) => (
        <LineRow
          key={line.line}
          line={line}
          evidence={evidence}
          taxYear={taxYear}
          quotations={quotations}
          scheduleO={scheduleO}
          open={openIds.has(line.line)}
          onToggle={() => toggle(line.line)}
        />
      ))}
    </>
  );
}

function Summary({ summary }: { summary: string | null }) {
  return summary === null ? <span className="text-text-tertiary">—</span> : <>{summary}</>;
}

function LineRow({
  line,
  evidence,
  taxYear,
  quotations,
  scheduleO,
  open,
  onToggle,
}: {
  line: Form990Line;
  evidence: Form990Evidence;
  taxYear: Form990TaxYear;
  quotations: readonly Form990Quotation[];
  scheduleO: Record<string, string | number> | null;
  open: boolean;
  onToggle: () => void;
}) {
  const summary = lineSummary(line, evidence, taxYear);
  const regionId = `form990-line-${line.line}-open`;
  return (
    <li
      // A line the address opens is brought into view below the console's own top bar.
      className="scroll-mt-[80px] border-t border-border-subtle first:border-t-0"
      data-testid={`form990-line-${line.line}`}
    >
      <button
        type="button"
        className={`${ROW_GRID} outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover`}
        aria-expanded={open}
        aria-controls={regionId}
        onClick={onToggle}
        data-testid={`form990-line-${line.line}-toggle`}
      >
        <span className={`pt-[1px] ${NUMBER}`}>{line.line}</span>
        <span className="flex min-w-0 flex-col gap-4">
          <span
            className={`font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong ${open ? '' : 'line-clamp-2'}`}
            data-testid={`form990-line-${line.line}-text`}
          >
            {line.text}
          </span>
          {/* The summary sits under the question until the row is wide enough for its own column. */}
          <span
            className={`${SECONDARY} md:hidden`}
            data-testid={`form990-line-${line.line}-summary-narrow`}
          >
            <Summary summary={summary} />
          </span>
        </span>
        <span
          className={`hidden ${SECONDARY} md:block`}
          data-testid={`form990-line-${line.line}-summary`}
        >
          <Summary summary={summary} />
        </span>
        <Icon
          icon={ChevronDown}
          size={18}
          aria-hidden
          className={`mt-[1px] text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {line.after !== null && line.after !== undefined ? (
        // The sentence the form prints after this line (1a's, 15b's), the IRS's words as printed.
        <p
          className={`${QUIET} -mt-4 pb-12 pl-[68px] pr-20 md:pl-[80px] md:pr-[412px]`}
          data-testid={`form990-line-${line.line}-after`}
        >
          {line.after}
        </p>
      ) : null}
      {open ? (
        <div id={regionId} className={OPENED} data-testid={regionId}>
          <Choices choices={line.choices} />
          {summary === null ? (
            <>
              <p className={SECONDARY}>{w.nothingRecorded}</p>
              <NothingRecordedWay
                line={line}
                evidence={evidence}
                testId={`form990-line-${line.line}-record-review`}
              />
            </>
          ) : (
            <LineEvidence
              line={line}
              evidence={evidence}
              taxYear={taxYear}
              quotations={quotations}
              scheduleO={scheduleO}
              testId={`form990-line-${line.line}-evidence`}
            />
          )}
          <div className="flex flex-wrap items-baseline gap-x-20 gap-y-8">
            <WhatTheInstructionsSay
              instructions={line.instructions}
              testId={`form990-line-${line.line}-instructions`}
            />
            {/* Line 11b's way to its Schedule O draft, while the return's facts are served. */}
            {line.evidence_kinds.includes('return_review') &&
            evidence.return_review !== null &&
            scheduleO !== null ? (
              <Link
                to={BOARD_PAGES.scheduleO}
                search={scheduleO}
                className={`font-sans text-s13 ${LINK}`}
                data-testid={`form990-line-${line.line}-schedule-o`}
              >
                {w.scheduleODraft}
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}
